'use client';

import { useEffect, useState, useCallback } from 'react';

interface ScrollFadeProps {
  /**
   * Target ref - the scrollable container to monitor
   */
  targetRef: React.RefObject<HTMLElement | null>;
  
  /**
   * Height of the fade gradient (in pixels)
   * @default 48
   */
  height?: number;
  
  /**
   * Position of the fade
   * @default 'bottom'
   */
  position?: 'top' | 'bottom';
  
  /**
   * Threshold in pixels - hide fade when within this distance from edge
   * @default 1
   */
  threshold?: number;
  
  /**
   * Custom className for additional styling
   */
  className?: string;
  
  /**
   * Background color for the gradient (hex color)
   * @default '#FFFFFF'
   */
  backgroundColor?: string;
}

/**
 * ScrollFade - A reusable component that displays a gradient fade at the edge of scrollable containers
 * 
 * Automatically shows/hides based on scroll position:
 * - Shows when there's more content to scroll
 * - Hides when user reaches the edge
 * 
 * @example
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null);
 * 
 * <div ref={scrollRef} className="overflow-y-auto">
 *   {content}
 *   <ScrollFade targetRef={scrollRef} />
 * </div>
 * ```
 */
export default function ScrollFade({
  targetRef,
  height = 48,
  position = 'bottom',
  threshold = 1,
  className = '',
  backgroundColor = '#FFFFFF',
}: ScrollFadeProps) {
  const [isVisible, setIsVisible] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const element = targetRef.current;
    if (!element) {
      setIsVisible(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = element;
    
    if (position === 'bottom') {
      // Show fade if not scrolled to bottom
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      setIsVisible(distanceFromBottom > threshold);
    } else {
      // Show fade if not scrolled to top
      setIsVisible(scrollTop > threshold);
    }
  }, [targetRef, position, threshold]);

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      checkScrollPosition();
    });

    // Add scroll listener
    element.addEventListener('scroll', checkScrollPosition);
    
    // Add resize observer to handle content/container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Use RAF for resize checks too
      requestAnimationFrame(checkScrollPosition);
    });
    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(rafId);
      element.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [checkScrollPosition, targetRef]);

  if (!isVisible) return null;

  const positionClasses = position === 'bottom' 
    ? 'bottom-0' 
    : 'top-0';

  // Create gradient with inline style for reliable rendering
  // Use rgb values to parse hex color for better gradient control
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
  };
  
  const rgb = hexToRgb(backgroundColor);
  const gradientDirection = position === 'bottom' ? '180deg' : '0deg';
  const backgroundStyle = {
    background: `linear-gradient(${gradientDirection}, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8) 50%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1) 100%)`,
    height: `${height}px`,
  };

  return (
    <div
      className={`absolute left-0 w-full z-20 pointer-events-none transition-opacity duration-300 ${positionClasses} ${className}`}
      style={backgroundStyle}
      aria-hidden="true"
    />
  );
}
